export const CLINIC_INSIGHTS_WINDOWS = [
  { key: "1d", label: "Yesterday" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
];

const CLINIC_INSIGHTS_SCORE_DEFINITIONS = {
  response_clarity:
    "How clear and easy to understand the clinic's replies feel to patients. A higher score is better.",
  continuity_of_handling:
    "How consistent the handoff and follow-through feel across the conversation. A higher score is better.",
  timeliness_perception:
    "How quickly patients feel their request is being handled or moved forward. A higher score is better.",
  operational_friction:
    "How smooth and low-friction the patient journey feels, with less confusion or back-and-forth. A higher score is better.",
};
const SNAPSHOT_CONTEXT_HIDDEN_KEYS = new Set(["clinic_id"]);
const SNAPSHOT_CONTEXT_GROUP_KEYS = new Set([
  "known_operational_signals",
  "current_dominant_topics",
  "recurring_operational_topics",
  "status_patterns",
]);
const SNAPSHOT_CONTEXT_LABELS = {
  currency: "Currency",
  clinic_name: "Clinic",
  known_operational_signals: "Operational signals",
  current_dominant_topics: "Dominant topics",
  recurring_operational_topics: "Recurring topics",
  manual_takeovers: "Manual takeovers",
  booking_friction_threads: "Booking friction",
  pending_threads: "Pending threads",
  friction_threads: "Operational friction",
};

function humanizeSnapshotContextLabel(value = "") {
  if (SNAPSHOT_CONTEXT_LABELS[value]) return SNAPSHOT_CONTEXT_LABELS[value];
  return String(value)
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function isSnapshotContextScalar(value) {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

function normalizeSnapshotContextItems(values = [], { humanizeItems = false } = {}) {
  if (!Array.isArray(values)) return [];
  return values
    .map((value) => String(value ?? "").trim())
    .filter(Boolean)
    .map((value) => (humanizeItems ? humanizeSnapshotContextLabel(value) : value));
}

export function getClinicInsightsSentimentBand(score) {
  if (score === null || score === undefined || score === "") return "Unavailable";
  const numericScore = Number(score);
  if (!Number.isFinite(numericScore)) return "Unavailable";
  if (numericScore >= 4) return "Strong";
  if (numericScore >= 2.5) return "Average";
  return "Fragile";
}

export function getClinicInsightsSentimentNeedleRotation(score) {
  if (score === null || score === undefined || score === "") return 0;
  const numericScore = Number(score);
  if (!Number.isFinite(numericScore)) return 0;
  const clampedScore = Math.min(5, Math.max(1, numericScore));
  return Math.round(-60 + ((clampedScore - 1) / 4) * 120);
}

export function getClinicInsightsSentimentGaugeArcClipPath() {
  return "inset(0 0 50% 0)";
}

export function getClinicInsightsScoreCardsGridClass() {
  return "grid gap-4 sm:grid-cols-2";
}

export function getClinicInsightsTopicSize(topic = {}) {
  const sharePercent = Number(topic.share_percent ?? 0);
  if (sharePercent >= 20) return "large";
  if (sharePercent >= 10) return "medium";
  return "small";
}

export function getClinicInsightsTopicBadge(source = "") {
  if (source === "patient") return "Patient signal";
  if (source === "clinic") return "Clinic response";
  if (source === "other") return "Other topics";
  return "Topic";
}

export function normalizeClinicInsightsWindow(value = "7d") {
  return CLINIC_INSIGHTS_WINDOWS.some((item) => item.key === value) ? value : "7d";
}

export function getClinicInsightsStatusTone(status = "") {
  if (status === "ready") return "success";
  if (status === "partial") return "warning";
  if (status === "failed") return "danger";
  return "muted";
}

export function getClinicInsightsScoreDefinition(scoreKey = "") {
  return CLINIC_INSIGHTS_SCORE_DEFINITIONS[scoreKey] ?? "";
}

export function getClinicInsightsConfidenceBadge(confidence = "") {
  if (confidence === "high") return "High confidence";
  if (confidence === "medium") return "Medium confidence";
  if (confidence === "low") return "Low confidence";
  return "";
}

export function getClinicInsightsChangeTone(direction = "") {
  if (direction === "improving") return "success";
  if (direction === "worsening") return "danger";
  return "muted";
}

export function buildClinicInsightsConversationHref(utilisateur = "") {
  return `/conversations/${encodeURIComponent(utilisateur)}`;
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizeClinicInsightsSubject(value) {
  const normalized = String(value ?? "").trim();
  return normalized.length >= 3 ? normalized : "";
}

function escapeClinicInsightsRegExp(value = "") {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function collectClinicInsightsPdfSubjectAliases(snapshot = {}) {
  const aliases = new Map();
  let nextAliasIndex = 1;

  const addAliasGroup = (values = []) => {
    const subjects = values
      .map((value) => normalizeClinicInsightsSubject(value))
      .filter(Boolean);
    if (subjects.length === 0) return null;

    const existingAlias = subjects.map((subject) => aliases.get(subject)).find(Boolean);
    const alias = existingAlias ?? `Patient ${nextAliasIndex++}`;
    for (const subject of subjects) {
      aliases.set(subject, alias);
    }
    return alias;
  };

  for (const thread of snapshot.at_risk_threads ?? []) {
    addAliasGroup([thread?.profile_name, thread?.utilisateur]);
  }

  const collectEvidenceAliases = (entries = []) => {
    for (const entry of entries ?? []) {
      addAliasGroup([entry?.utilisateur]);
    }
  };

  const collectThemeAliases = (themes = []) => {
    for (const theme of themes ?? []) {
      collectEvidenceAliases(theme?.evidence);
    }
  };

  collectThemeAliases(snapshot.patient_voice_themes);
  collectThemeAliases(snapshot.clinic_response_themes);

  for (const topic of snapshot.topic_map ?? []) {
    collectEvidenceAliases(topic?.evidence);
  }

  return aliases;
}

function anonymizeClinicInsightsText(value, aliases) {
  let anonymized = String(value ?? "");
  const sortedAliases = Array.from(aliases.entries()).sort(
    ([left], [right]) => right.length - left.length,
  );

  for (const [subject, alias] of sortedAliases) {
    anonymized = anonymized.replace(new RegExp(escapeClinicInsightsRegExp(subject), "gi"), alias);
  }

  return anonymized
    .replace(/whatsapp:[^\s,;)"']+/gi, "patient contact")
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "patient email")
    .replace(/\+\d[\d\s().-]{6,}\d/g, "patient phone")
    .replace(/\b\d{8,}\b/g, "patient phone");
}

function anonymizeClinicInsightsPdfValue(value, aliases) {
  if (typeof value === "string") return anonymizeClinicInsightsText(value, aliases);
  if (Array.isArray(value)) {
    return value.map((item) => anonymizeClinicInsightsPdfValue(item, aliases));
  }
  if (!isPlainObject(value)) return value;

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      anonymizeClinicInsightsPdfValue(item, aliases),
    ]),
  );
}

function anonymizeClinicInsightsEvidenceItem(item, aliases) {
  const anonymized = anonymizeClinicInsightsPdfValue(item, aliases);
  const alias = aliases.get(normalizeClinicInsightsSubject(item?.utilisateur));
  if (alias && isPlainObject(anonymized)) {
    anonymized.utilisateur = alias;
  }
  return anonymized;
}

function anonymizeClinicInsightsTheme(theme, aliases) {
  const anonymized = anonymizeClinicInsightsPdfValue(theme, aliases);
  if (isPlainObject(anonymized) && Array.isArray(theme?.evidence)) {
    anonymized.evidence = theme.evidence.map((item) =>
      anonymizeClinicInsightsEvidenceItem(item, aliases),
    );
  }
  return anonymized;
}

function anonymizeClinicInsightsTopic(topic, aliases) {
  const anonymized = anonymizeClinicInsightsPdfValue(topic, aliases);
  if (isPlainObject(anonymized) && Array.isArray(topic?.evidence)) {
    anonymized.evidence = topic.evidence.map((item) =>
      anonymizeClinicInsightsEvidenceItem(item, aliases),
    );
  }
  return anonymized;
}

function anonymizeClinicInsightsAtRiskThread(thread, aliases) {
  const anonymized = anonymizeClinicInsightsPdfValue(thread, aliases);
  const alias =
    aliases.get(normalizeClinicInsightsSubject(thread?.profile_name)) ??
    aliases.get(normalizeClinicInsightsSubject(thread?.utilisateur));

  if (alias && isPlainObject(anonymized)) {
    anonymized.utilisateur = alias;
    anonymized.profile_name = alias;
  }

  return anonymized;
}

export function buildClinicInsightsPdfExportSnapshot(snapshot = {}) {
  if (!isPlainObject(snapshot)) return snapshot;

  const aliases = collectClinicInsightsPdfSubjectAliases(snapshot);
  const exportSnapshot = anonymizeClinicInsightsPdfValue(snapshot, aliases);

  if (Array.isArray(snapshot.patient_voice_themes)) {
    exportSnapshot.patient_voice_themes = snapshot.patient_voice_themes.map((theme) =>
      anonymizeClinicInsightsTheme(theme, aliases),
    );
  }
  if (Array.isArray(snapshot.clinic_response_themes)) {
    exportSnapshot.clinic_response_themes = snapshot.clinic_response_themes.map((theme) =>
      anonymizeClinicInsightsTheme(theme, aliases),
    );
  }
  if (Array.isArray(snapshot.topic_map)) {
    exportSnapshot.topic_map = snapshot.topic_map.map((topic) =>
      anonymizeClinicInsightsTopic(topic, aliases),
    );
  }
  if (Array.isArray(snapshot.at_risk_threads)) {
    exportSnapshot.at_risk_threads = snapshot.at_risk_threads.map((thread) =>
      anonymizeClinicInsightsAtRiskThread(thread, aliases),
    );
  }

  return exportSnapshot;
}

export function buildClinicInsightsSnapshotContext(context = {}) {
  if (!context || typeof context !== "object" || Array.isArray(context)) {
    return {
      metadata: [],
      groups: [],
      stats: [],
    };
  }

  const metadata = [];
  const metadataEntries = Object.entries(context)
    .filter(([key, value]) => {
      if (SNAPSHOT_CONTEXT_HIDDEN_KEYS.has(key) || SNAPSHOT_CONTEXT_GROUP_KEYS.has(key)) return false;
      if (!isSnapshotContextScalar(value)) return false;
      return String(value).trim() !== "";
    })
    .sort(([leftKey], [rightKey]) => {
      if (leftKey === "currency") return -1;
      if (rightKey === "currency") return 1;
      return leftKey.localeCompare(rightKey);
    });

  for (const [key, value] of metadataEntries) {
    metadata.push({
      label: humanizeSnapshotContextLabel(key),
      value: String(value),
    });
  }

  const groups = [];
  const operationalSignals = normalizeSnapshotContextItems(context.known_operational_signals, {
    humanizeItems: true,
  });
  if (operationalSignals.length > 0) {
    groups.push({
      label: humanizeSnapshotContextLabel("known_operational_signals"),
      items: operationalSignals,
    });
  }

  const dominantTopics = normalizeSnapshotContextItems(context.current_dominant_topics);
  if (dominantTopics.length > 0) {
    groups.push({
      label: humanizeSnapshotContextLabel("current_dominant_topics"),
      items: dominantTopics,
    });
  }

  const recurringTopics = normalizeSnapshotContextItems(context.recurring_operational_topics);
  if (recurringTopics.length > 0) {
    groups.push({
      label: humanizeSnapshotContextLabel("recurring_operational_topics"),
      items: recurringTopics,
    });
  }

  const stats = [];
  if (context.status_patterns && typeof context.status_patterns === "object" && !Array.isArray(context.status_patterns)) {
    for (const [key, value] of Object.entries(context.status_patterns)) {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue) || numericValue <= 0) continue;
      stats.push({
        label: humanizeSnapshotContextLabel(key),
        value: numericValue,
      });
    }
  }

  return {
    metadata,
    groups,
    stats,
  };
}
